import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Video, Trash2, Copy, AlertCircle, CheckCircle, Loader, FolderPlus, ChevronLeft, Folder, Edit2, Play } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSearchParams } from 'react-router-dom';
import { folderEvents } from '../lib/events';
import VideoMetadataModal from './VideoMetadataModal';
import { compressVideo, generateThumbnail, getVideoDuration, getVideoResolution } from '../lib/videoCompression';

interface Video {
  id: string;
  title: string;
  description?: string;
  url: string;
  thumbnail_url?: string;
  duration?: number;
  size: number;
  format?: string;
  resolution?: string;
  folder?: string;
  created_at: string;
  metadata?: any;
}

interface FolderInfo {
  name: string;
  count: number;
}

export default function AdminVideos() {
  const [searchParams] = useSearchParams();
  const folderParam = searchParams.get('folder');
  
  const [videos, setVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [currentFolder, setCurrentFolder] = useState<string>(folderParam || '');
  const [showNewFolderInput, setShowNewFolderInput] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [folders, setFolders] = useState<FolderInfo[]>([]);
  const [editingVideo, setEditingVideo] = useState<Video | null>(null);
  const [draggedVideo, setDraggedVideo] = useState<Video | null>(null);

  useEffect(() => {
    setCurrentFolder(folderParam || '');
  }, [folderParam]);

  useEffect(() => {
    fetchVideos();
    fetchFolders();
  }, [currentFolder]);

  const fetchFolders = async () => {
    try {
      // Get all videos from database to count by folder
      const { data: dbVideos, error: dbError } = await supabase
        .from('videos')
        .select('folder');

      if (dbError) throw dbError;

      // Count videos by folder
      const folderCounts = (dbVideos || []).reduce((acc: Record<string, number>, video) => {
        if (video.folder) {
          const folderName = String(video.folder);
          acc[folderName] = (acc[folderName] || 0) + 1;
        }
        return acc;
      }, {});

      // List all folders in storage
      const { data: storageData, error: storageError } = await supabase.storage
        .from('videos')
        .list('', {
          limit: 1000,
          sortBy: { column: 'name', order: 'asc' }
        });

      if (storageError) throw storageError;

      // Create folder list with counts
      const folderList = (storageData || [])
        .filter(item => !String(item.name || '').includes('.'))
        .map(item => ({
          name: String(item.name || ''),
          count: folderCounts[String(item.name || '')] || 0
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      setFolders(folderList);
    } catch (err) {
      console.error('Error fetching folders:', err);
      setFolders([]);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) {
      setError('Folder name cannot be empty');
      return;
    }

    const sanitizedFolderName = newFolderName
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-')
      .replace(/-+/g, '-');

    try {
      setError(null);
      
      // Create an empty video file as folder marker
      const emptyMP4 = new Blob([new Uint8Array([0x00])], { type: 'video/mp4' });
      const { error } = await supabase.storage
        .from('videos')
        .upload(`${sanitizedFolderName}/.folder`, emptyMP4);

      if (error) throw error;

      setShowNewFolderInput(false);
      setNewFolderName('');
      await fetchFolders();
      folderEvents.emit();
      setSuccessMessage('Folder created successfully');
    } catch (err) {
      console.error('Error creating folder:', err);
      setError('Failed to create folder');
    }
  };

  const fetchVideos = async () => {
    try {
      setLoading(true);
      
      // Fetch videos from database
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Filter by current folder
      const filteredVideos = (data || []).filter(video => {
        if (currentFolder) {
          return String(video.folder || '') === currentFolder;
        }
        return !video.folder;
      });

      setVideos(filteredVideos);
    } catch (err) {
      console.error('Error fetching videos:', err);
      setError('Failed to load videos');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    setUploading(true);
    setError(null);
    setSuccessMessage(null);

    try {
      for (const file of acceptedFiles) {
        // Check file size before compression
        if (file.size > 500 * 1024 * 1024) { // 500MB
          throw new Error(`File ${file.name} exceeds maximum size of 500MB`);
        }

        // Get video metadata before compression
        const duration = await getVideoDuration(file);
        const resolution = await getVideoResolution(file);

        // Generate thumbnail
        const thumbnailBlob = await generateThumbnail(file);
        const thumbnailFile = new File([thumbnailBlob], `thumbnail-${file.name}.jpg`, { type: 'image/jpeg' });

        // Compress video
        const compressedBlob = await compressVideo(file);
        const compressedFile = new File([compressedBlob], file.name, { type: 'video/mp4' });

        // Upload thumbnail
        const thumbnailPath = currentFolder 
          ? `${currentFolder}/thumbnails/${thumbnailFile.name}`
          : `thumbnails/${thumbnailFile.name}`;

        const { error: thumbnailError, data: thumbnailData } = await supabase.storage
          .from('assets')
          .upload(thumbnailPath, thumbnailFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (thumbnailError) throw thumbnailError;

        const { data: { publicUrl: thumbnailUrl } } = supabase.storage
          .from('assets')
          .getPublicUrl(thumbnailPath);

        // Upload compressed video
        const sanitizedName = `${Date.now()}-${file.name.toLowerCase().replace(/[^a-z0-9.]/g, '-')}`;
        const filePath = currentFolder 
          ? `${currentFolder}/${sanitizedName}`
          : sanitizedName;

        const { error: uploadError } = await supabase.storage
          .from('videos')
          .upload(filePath, compressedFile, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('videos')
          .getPublicUrl(filePath);

        const { error: dbError } = await supabase
          .from('videos')
          .insert([{
            title: file.name,
            url: publicUrl,
            thumbnail_url: thumbnailUrl,
            size: compressedFile.size,
            duration,
            resolution,
            folder: currentFolder || null,
            format: compressedFile.type
          }]);

        if (dbError) throw dbError;
      }

      await fetchVideos();
      await fetchFolders();
      setSuccessMessage('Videos uploaded successfully');
    } catch (err) {
      console.error('Error uploading videos:', err);
      setError(err instanceof Error ? err.message : 'Failed to upload videos');
    } finally {
      setUploading(false);
    }
  }, [currentFolder]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.webm', '.ogg']
    },
    maxSize: 500 * 1024 * 1024 // 500MB
  });

  const handleDelete = async (video: Video) => {
    if (!confirm('Are you sure you want to delete this video? This action cannot be undone.')) return;

    try {
      // Delete video from storage
      const filePath = video.url.split('/videos/')[1];
      if (!filePath) throw new Error('Invalid file path');

      const { error: videoStorageError } = await supabase.storage
        .from('videos')
        .remove([filePath]);

      if (videoStorageError) throw videoStorageError;

      // Delete thumbnail if it exists
      if (video.thumbnail_url) {
        const thumbnailPath = video.thumbnail_url.split('/assets/')[1];
        if (thumbnailPath) {
          await supabase.storage
            .from('assets')
            .remove([thumbnailPath]);
        }
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from('videos')
        .delete()
        .eq('id', video.id);

      if (dbError) throw dbError;

      // Refresh data
      await fetchVideos();
      await fetchFolders();
      setSuccessMessage('Video deleted successfully');
    } catch (err) {
      console.error('Error deleting video:', err);
      setError('Failed to delete video');
    }
  };

  const handleDeleteFolder = async (folderName: string) => {
    if (!confirm(`Are you sure you want to delete the folder "${folderName}" and all its contents? This action cannot be undone.`)) return;

    try {
      // List all files in the folder
      const { data: files, error: listError } = await supabase.storage
        .from('videos')
        .list(folderName);

      if (listError) throw listError;

      // Delete all files in storage
      if (files && files.length > 0) {
        const filePaths = files.map(file => `${folderName}/${file.name}`);
        const { error: deleteError } = await supabase.storage
          .from('videos')
          .remove(filePaths);

        if (deleteError) throw deleteError;
      }

      // Delete folder marker
      await supabase.storage
        .from('videos')
        .remove([`${folderName}/.folder`]);

      // Delete all videos in this folder from database
      const { error: dbError } = await supabase
        .from('videos')
        .delete()
        .eq('folder', folderName);

      if (dbError) throw dbError;

      // Refresh data
      await fetchVideos();
      await fetchFolders();
      setSuccessMessage('Folder deleted successfully');
    } catch (err) {
      console.error('Error deleting folder:', err);
      setError('Failed to delete folder');
    }
  };

  const handleMetadataSave = async (video: Video, metadata: any) => {
    try {
      const { error } = await supabase
        .from('videos')
        .update({
          title: metadata.title,
          description: metadata.description,
          thumbnail_url: metadata.thumbnail_url,
          metadata: {
            ...video.metadata,
            tags: metadata.tags,
            category: metadata.category,
            language: metadata.language,
            custom: metadata.custom
          }
        })
        .eq('id', video.id);

      if (error) throw error;

      setEditingVideo(null);
      await fetchVideos();
      setSuccessMessage('Video metadata updated successfully');
    } catch (err) {
      console.error('Error updating video metadata:', err);
      setError('Failed to update video metadata');
    }
  };

  const handleDragStart = (video: Video) => {
    setDraggedVideo(video);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, targetFolder: string) => {
    e.preventDefault();
    if (!draggedVideo) return;

    // Don't move if the video is already in the target folder
    if (draggedVideo.folder === targetFolder) return;

    try {
      setError(null);
      
      // Get the old file path
      const oldPath = draggedVideo.url.split('/videos/')[1];
      if (!oldPath) throw new Error('Invalid file path');

      // Create the new file path
      const fileName = oldPath.split('/').pop();
      if (!fileName) throw new Error('Invalid file name');
      const newPath = targetFolder ? `${targetFolder}/${fileName}` : fileName;

      // Copy the file to the new location
      const { error: copyError } = await supabase.storage
        .from('videos')
        .copy(oldPath, newPath);

      if (copyError) throw copyError;

      // Delete the old file
      const { error: deleteError } = await supabase.storage
        .from('videos')
        .remove([oldPath]);

      if (deleteError) throw deleteError;

      // Get the new public URL
      const { data: { publicUrl } } = supabase.storage
        .from('videos')
        .getPublicUrl(newPath);

      // Update the video record in the database
      const { error: updateError } = await supabase
        .from('videos')
        .update({
          url: publicUrl,
          folder: targetFolder || null
        })
        .eq('id', draggedVideo.id);

      if (updateError) throw updateError;

      await fetchVideos();
      await fetchFolders();
      setSuccessMessage('Video moved successfully');
    } catch (err) {
      console.error('Error moving video:', err);
      setError('Failed to move video');
    } finally {
      setDraggedVideo(null);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'Unknown';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-12 w-12 text-[#F98B3D] animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-4">
          <h1 className="text-2xl font-bold text-gray-900">Videos</h1>
          {currentFolder && (
            <button
              onClick={() => setCurrentFolder('')}
              className="inline-flex items-center text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              Back to root
            </button>
          )}
        </div>
        <button
          onClick={() => setShowNewFolderInput(true)}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <FolderPlus className="h-5 w-5 mr-2" />
          New Folder
        </button>
      </div>

      {showNewFolderInput && (
        <div className="mb-6 flex items-center space-x-4">
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            placeholder="Enter folder name"
            className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-[#F98B3D] focus:ring focus:ring-[#F98B3D] focus:ring-opacity-50"
          />
          <button
            onClick={createFolder}
            className="px-4 py-2 bg-[#F98B3D] text-white rounded-md hover:bg-[#e07a2c]"
          >
            Create
          </button>
          <button
            onClick={() => {
              setShowNewFolderInput(false);
              setNewFolderName('');
            }}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      )}

      {error && (
        <div className="mb-4 bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 bg-green-50 border-l-4 border-green-400 p-4">
          <div className="flex">
            <CheckCircle className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">{successMessage}</p>
            </div>
          </div>
        </div>
      )}

      {!currentFolder && folders.length > 0 && (
        <div className="mb-8">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Folders</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {folders.map((folder) => (
              <div
                key={folder.name}
                className={`bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between ${
                  draggedVideo ? 'cursor-copy' : ''
                }`}
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, folder.name)}
              >
                <button
                  onClick={() => setCurrentFolder(folder.name)}
                  className="flex items-center text-gray-900 hover:text-[#F98B3D]"
                >
                  <Folder className="h-5 w-5 mr-2" />
                  <span className="flex-1">{folder.name}</span>
                  <span className="ml-2 text-sm text-gray-500">({folder.count})</span>
                </button>
                <button
                  onClick={() => handleDeleteFolder(folder.name)}
                  className="text-red-600 hover:text-red-900"
                  title="Delete folder"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div 
        {...getRootProps()} 
        className={`mb-8 border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
          isDragActive ? 'border-[#F98B3D] bg-orange-50' : 'border-gray-300 hover:border-[#F98B3D]'
        }`}
      >
        <input {...getInputProps()} />
        <Video className="h-12 w-12 mx-auto mb-4 text-gray-400" />
        <p className="text-gray-600">
          {isDragActive
            ? 'Drop the videos here...'
            : `Drag & drop videos here, or click to select files${currentFolder ? ` (uploading to /${currentFolder})` : ''}`}
        </p>
        <p className="text-sm text-gray-500 mt-2">
          Supported formats: MP4, WebM, OGG (max 500MB)
        </p>
      </div>

      {uploading && (
        <div className="mb-4 bg-blue-50 border-l-4 border-blue-400 p-4">
          <div className="flex">
            <Loader className="h-5 w-5 text-blue-400 animate-spin" />
            <div className="ml-3">
              <p className="text-sm text-blue-700">Uploading videos...</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {videos.map((video) => (
          <div 
            key={video.id} 
            className="bg-white rounded-lg border border-gray-200 overflow-hidden cursor-move"
            draggable
            onDragStart={() => handleDragStart(video)}
          >
            <div className="aspect-video relative group">
              {video.thumbnail_url ? (
                <img 
                  src={video.thumbnail_url} 
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                  <Video className="h-12 w-12 text-gray-400" />
                </div>
              )}
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity flex items-center justify-center opacity-0 group-hover:opacity-100">
                <div className="flex space-x-2">
                  <a
                    href={video.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-white hover:text-[#F98B3D] transition-colors"
                    title="Play video"
                  >
                    <Play className="h-5 w-5" />
                  </a>
                  <button
                    onClick={() => setEditingVideo(video)}
                    className="p-2 text-white hover:text-[#F98B3D] transition-colors"
                    title="Edit metadata"
                  >
                    <Edit2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(video)}
                    className="p-2 text-white hover:text-red-500 transition-colors"
                    title="Delete video"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-4">
              <h3 className="font-medium text-gray-900 truncate">{video.title}</h3>
              <div className="mt-2 text-sm text-gray-500 space-y-1">
                <p>Size: {formatFileSize(video.size)}</p>
                {video.duration && <p>Duration: {formatDuration(video.duration)}</p>}
                {video.resolution && <p>Resolution: {video.resolution}</p>}
              </div>
              {video.description && (
                <p className="mt-2 text-sm text-gray-600 line-clamp-2">{video.description}</p>
              )}
            </div>
          </div>
        ))}
      </div>

      {videos.length === 0 && !loading && (
        <div 
          className={`text-center py-12 ${draggedVideo ? 'border-2 border-dashed border-gray-300 rounded-lg' : ''}`}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, '')}
        >
          <Video className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">
            {currentFolder 
              ? `No videos found in folder /${currentFolder}`
              : 'No videos found. Upload some videos to get started.'}
          </p>
          {draggedVideo && !currentFolder && (
            <p className="text-sm text-gray-500 mt-2">Drop here to move to root folder</p>
          )}
        </div>
      )}

      {editingVideo && (
        <VideoMetadataModal
          video={editingVideo}
          onClose={() => setEditingVideo(null)}
          onSave={handleMetadataSave}
        />
      )}
    </div>
  );
}